@if (hasUnsignedOnboardingForms) {
  <div class="onboarding-banner"
    (click)="navigateToUnsignedForms()"
    role="button"
    tabindex="0">
    <mat-icon class="banner-icon">assignment_late</mat-icon>
    <span class="banner-text">{{ 'homeCard.unsignedOnboardingForms' | translate }}</span>
    <button mat-button class="banner-view-btn"
      (click)="$event.stopPropagation(); navigateToUnsignedForms()">
      {{ 'homeCard.goSignForms' | translate }}
      <mat-icon iconPositionEnd>arrow_forward</mat-icon>
    </button>
  </div>
}

<div class="home-layout">
  <div class="widget-container">
    <div class="widget">
      <app-weather-widget [address]="address" [color]="'primary'" appFlyIn></app-weather-widget>
    </div>
    <div class="widget">
      <ri-safety-widget [projectId]="projectId$|async"  *hasPermission="[permissions.CanManageProjects, permissions.ManageOwnProjects, permissions.SafetyEventsPermissions.CanViewSafetyEvents, permissions.CanAddHourEntries]" appFlyIn></ri-safety-widget>
    </div>
    <!-- Notifications Widget (hidden on small screens — shown as a card in Admin section) -->
    <div class="widget announcements-widget-mobile">
      <ri-announcements-widget appFlyIn></ri-announcements-widget>
    </div>
  </div>

  <div class="card-container">

    <!-- Auth pending: skeleton placeholder cards -->
    @if (isLoading) {
      @for (_ of skeletonCards; track $index) {
        <div class="home-card-skeleton"></div>
      }
    }

    <!-- Real cards: shown after auth check resolves -->
    @if (!isLoading) {
      <!-- ── Operations ──────────────────────────────────────────────── -->
      <div class="section-divider">
        <span>{{ 'homeCard.sectionOperations' | translate }}</span>
      </div>
      <ri-home-card [title]="'homeCard.arrivalsTitle'"
        *hasPermission="[permissions.UserPermissions.CanSeeCalendar]"
        (clicked)="handleCalClicked()"
        iconName="calendar_month" [badgeText]="getBadgeCount()" [badgeColor]="'accent'"
        appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.newArrival'"
        *hasPermission="[permissions.UserPermissions.CanMakeReservations]"
        [routeTo]="['projects','arrivals','add']"
        iconName="local_shipping"
        [disabled]="hasUnsignedOnboardingForms"
        [disabledMessage]="!arrivalCalendarEnabled ? ('PROJECT.SETTINGS.ARRIVAL_DISABLED' | translate) : ''"
        appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.liftReservation'"
        *hasPermission="[permissions.UserPermissions.CanMakeReservations]"
        (clicked)="handleEquipmentClick()"
        iconName="arrow_upward"
        [disabled]="hasUnsignedOnboardingForms"
        [disabledMessage]="!liftCalendarEnabled ? ('PROJECT.SETTINGS.LIFT_DISABLED' | translate) : ''"
        appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.safety'"
        *hasPermission="[permissions.CanAddHourEntries]"
        [routeTo]="['https://app.falcony.io/ltqrakennusoy/links/avoin']"
        iconName="engineering"
        appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.worktime'"
        *hasPermission="[permissions.CanAddHourEntries]"
        [routeTo]="['/worktime','roster']"
        iconName="schedule"
        [disabled]="hasUnsignedOnboardingForms" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.shop'"
        *hasPermission="[permissions.CanMakeOrders]"
        [routeTo]="['/warehouses','shop']"
        iconName="shopping_cart"
        [disabled]="hasUnsignedOnboardingForms" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.cleaningPlan'"
        *hasPermission="[permissions.CleaningPlanPermissions.CanReadCleaningPlan, permissions.CleaningPlanPermissions.CanEditCleaningPlan]"
        (clicked)="handleCleaningPlanClick()"
        iconName="cleaning_services"
        [disabled]="hasUnsignedOnboardingForms" appFlyIn>
      </ri-home-card>
      <ri-home-card
        [title]="'ANNOUNCEMENTS.INBOX_TITLE'"
        [routeTo]="['/announcements','my']"
        iconName="inbox"
        [badgeText]="(attentionCount$ | async) || null"
        [badgeColor]="'accent'"
        appFlyIn>
      </ri-home-card>
      <!-- ── Management ─────────────────────────────────────────────── -->
      <div class="section-divider">
        <span>{{ 'homeCard.sectionManagement' | translate }}</span>
      </div>
      <ri-home-card [title]="'homeCard.onboarding'"
        *hasPermission="[permissions.OnboardingPermissions.CanSignOnboarding, permissions.OnboardingPermissions.CanManageOnboarding]"
        [routeTo]="['onboarding']"
        iconName="receipt_long"
        appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.projects'"
        *hasPermission="permissions.CanViewProjects"
        [routeTo]="['/projects']"
        iconName="domain"
        [disabled]="hasUnsignedOnboardingForms" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.companies'"
        *hasPermission="permissions.CanManageCompanies"
        [routeTo]="['/companies']"
        iconName="work"
        [disabled]="hasUnsignedOnboardingForms" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.billing'"
        *hasPermission="[permissions.CanCreateSummary, permissions.CanConfirmSummary, permissions.CanConfirmAllSummaries]"
        (clicked)="handleBillingClick()"
        iconName="receipt_long" [badgeText]="(unconfirmedCount$|async)||null" [badgeColor]="'accent'" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.warehouses'"
        *hasPermission="permissions.WarehouseListPermissions"
        [routeTo]="['/warehouses']"
        iconName="warehouse" [badgeText]="newOrdersCount" [badgeColor]="'accent'"
        [disabled]="hasUnsignedOnboardingForms" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.reports'"
        *hasPermission="[permissions.CanManageHours, permissions.WorkClassificationPermissions.CanManageWorkClassification]"
        [routeTo]="['/worktime','reports']"
        iconName="trending_up"
        [disabled]="hasUnsignedOnboardingForms" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.paceProduction'"
        *hasPermission="[permissions.PaceProductionPermissions.CanManagePaceProduction]"
        [routeTo]="['/pace']"
        iconName="speed" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.diary'"
        *hasPermission="[permissions.CanSeeDebugInfo, permissions.DiaryPermissions.CanViewDiary]"
        [routeTo]="['/diary']"
        iconName="menu_book"
        appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.notificationsTitle'"
        *hasPermission="[permissions.AnnouncementPermissions.CanManageAnnouncements]"
        [routeTo]="['/announcements']"
        iconName="campaign"
        appFlyIn>
      </ri-home-card>
      <!-- ── Admin ───────────────────────────────────────────────────── -->
      <div class="section-divider">
        <span>{{ 'homeCard.sectionAdmin' | translate }}</span>
      </div>
      <ri-home-card [title]="'homeCard.checkHours'"
        *hasPermission="[permissions.CanAcceptEntry, permissions.CanSeeContractorHours]"
        [routeTo]="['/worktime','reports']"
        iconName="event_available" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.hourSummary'"
        *hasPermission="[permissions.CanAcceptEntry, permissions.CanSeeContractorHours]"
        [routeTo]="['/worktime','reports','hours']"
        iconName="summarize" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.overtimeRequest'"
        *hasPermission="[permissions.OvertimeRequestPermissions.CanAddOvertimeRequests,permissions.OvertimeRequestPermissions.CanManageOvertimeRequests]"
        [routeTo]="['/overtime']"
        iconName="pending_actions"
        [disabled]="hasUnsignedOnboardingForms" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.userManagement'"
        *hasPermission="[permissions.UserPermissions.ManageUsers, permissions.UserPermissions.CanSeeProjectUsers]"
        [routeTo]="['/user-management']"
        iconName="manage_accounts" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.permissions'"
        *hasPermission="permissions.UserPermissions.CanManageUserRoles"
        [routeTo]="['/user-management','permissions']"
        iconName="settings" appFlyIn>
      </ri-home-card>
      <!-- Järjestelmävalvoja only: same lookup as the systemAdminGuard on /feature-switches. The lookup
           (api/RolePermission/caller) needs CanManageUserRoles, so only users holding it ask. -->
      <ng-container *hasPermission="permissions.UserPermissions.CanManageUserRoles">
        <ri-home-card *riSystemAdminOnly [title]="'FEATURE_SWITCHES.TITLE'"
          [routeTo]="['/feature-switches']"
          iconName="toggle_on" appFlyIn>
        </ri-home-card>
      </ng-container>
      <ri-home-card [title]="'homeCard.aiPlayground'"
        *hasPermission="permissions.CanAccessAIPlayground"
        [routeTo]="['/ai-playground']"
        iconName="smart_toy"
        [disabled]="hasUnsignedOnboardingForms" appFlyIn>
      </ri-home-card>
      <ri-home-card [title]="'homeCard.analytics'"
        *hasPermission="[permissions.CanViewAnalytics]"
        [routeTo]="['/analytics']"
        iconName="analytics"
        appFlyIn>
      </ri-home-card>
      <!-- Gated on what /analytics/project actually accepts, not on CanViewAnalytics. It used to
      show for analytics admins the route refuses, and hide from the users it admits. -->
      <ri-home-card [title]="'homeCard.projectAnalytics'"
        *hasPermission="[permissions.CanViewProjectAnalytics]"
        [routeTo]="['/analytics/project']"
        iconName="bar_chart"
        appFlyIn>
      </ri-home-card>
      }<!-- /!isLoading -->

    </div>
  </div>

